import * as React from "react";
import { useState, useEffect, createRef } from 'react';
import { useAppSelector, useAppDispatch } from "../store/index";
import { useNavigate, useParams } from "react-router-dom";
import { RootState } from "../reducers/index";
import { IMapOptions } from "../interfaces/mapoptions";
import { showPoi, selectMapDisplayMode } from "../reducers/poi";
import { IGoogleMapsSlice } from "../reducers/googlemaps";
import { setHalfway } from "../reducers/halfway";
import { getTravelMode, getTotalDistance, getMidPoint } from "../api/googlemaps";
import { getEndIcon, getSpriteIcon } from "../api/googlemapicon";
import { composePathPoi } from "../api/utility";
import { getMidpointMarker } from "../api/googlemapmarker";
import { getPoiLatLng } from "../api/poisearch";
import * as MapDisplayMode from "../constants/mapdisplaymode";
import DynamicMapGeocode from "./dynamicmapgeocode";

interface IGoogleMapResultsProps {
    setMapError(status: google.maps.DirectionsStatus): void;
}

// An alternative wrapper: https://www.npmjs.com/package/@googlemaps/react-wrapper

const GoogleMapResults = (props: IGoogleMapResultsProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { adr1, adr2, poiname, travel } = useParams();
    const myRef = createRef<HTMLDivElement>();
    const mapoptions = useAppSelector<IMapOptions>((x: RootState) => x.mapoptions);
    const mapDisplayMode = useAppSelector<number>(selectMapDisplayMode);
    const poiList = useAppSelector((x: RootState) => x.poi.list);
    const [map, setMap] = useState<google.maps.Map>(null);
    const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer>(null);
    const [directionsService, setDirectionsService] = useState<google.maps.DirectionsService>(null);
    const [routeMarker1, setRouteMarker1] = useState<google.maps.Marker>(null);
    const [routeMarker2, setRouteMarker2] = useState<google.maps.Marker>(null);
    const [halfwayMarker, setHalfwayMarker] = useState<google.maps.Marker>(null);
    const [poiMarkers, setPoiMarkers] = useState<Array<google.maps.Marker>>([]);
    const googlemapsstate = useAppSelector<IGoogleMapsSlice>((x: RootState) => x.googlemaps);

    // Run on startup only .. create all the google map objects we need
    useEffect(() => {
        if (googlemapsstate.isLoaded) {
            const mymap = createMap();
            setMap(mymap);
            //var map = this.state.mapManager.map;
            createDirectionsDisplay(mymap);
        }
    }, [googlemapsstate]);

    // run once we have the services we need (map & directions services)
    useEffect(() => {
        if (!directionsRenderer || !directionsService || !map) {
            return;
        }

        let halfwayCalculated = false;

        // This recomputes the midpoint once the direction are displayed
        // maybe need to flag this not to do it again if we've already done this?
        google.maps.event.addListener(directionsRenderer, 'directions_changed', function () {
            clearStartEndMarkers();
            const directions = directionsRenderer.getDirections();
            //console.log(directions.geocoded_waypoints);
            showStartEndMarkers(directions);
            if (!halfwayCalculated) {
                clearPoiMarkers();
                computeMidPoint(directions);
                halfwayCalculated = true;
            } else {
                if (mapDisplayMode === MapDisplayMode.POI || mapDisplayMode === MapDisplayMode.RESULTS) {
                    showPoiMarkers();
                }
            }
        });

        calcRouteHalfway();
    }, [map, directionsRenderer, directionsService]);


    useEffect(() => {
        if (poiList.length > 0) {
            showPoiMarkers();
        }
    }, [poiList]); // When the list of Pois changes, then we show the markers

    const createMap = (): google.maps.Map => {
        const mapOptions: google.maps.MapOptions = {
            center: { lat: 34.0522342, lng: -118.2436849 },
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            zoom: 10,
            scrollwheel: false
        };

        const mymap = new google.maps.Map(myRef.current, mapOptions);

        mymap.addListener("resize", function () {
            const center = mymap.getCenter();
            google.maps.event.trigger(mymap, "resize");
            mymap.setCenter(center);
        });

        return mymap;
    }

    const createDirectionsDisplay = (mymap: google.maps.Map) => {
        const rendererOptions: google.maps.DirectionsRendererOptions = {
            draggable: false,
            suppressMarkers: true
        };
        const dr = new google.maps.DirectionsRenderer(rendererOptions);
        const ds = new google.maps.DirectionsService();
        dr.setMap(mymap);

        setDirectionsRenderer(dr);
        setDirectionsService(ds);
    }

    // show all POI Markers... they are already in the props
    const showPoiMarkers = () => {
        let pm: Array<google.maps.Marker> = [];
        poiList.forEach(function (poi) {
            const latlng = getPoiLatLng(poi);
            const marker = dropPin(latlng, poi.poiindex);
            pm.push(marker);
            marker.addListener('click', () => { clickMarker(poi); });
        });
        setPoiMarkers(pm);
    }

    // Clear any markers
    const clearStartEndMarkers = () => {
        if (routeMarker1) {
            routeMarker1.setMap(null);
            setRouteMarker1(null);
        }
        if (routeMarker2) {
            routeMarker2.setMap(null);
            setRouteMarker2(null);
        }
        if (halfwayMarker) {
            halfwayMarker.setMap(null);
            setHalfwayMarker(null);
        }
    }

    const showStartEndMarkers = (directionResult: google.maps.DirectionsResult) => {
        const route = directionResult.routes[0];
        const leg = route.legs[0];

        const loc1 = leg.start_location;
        const loc2 = leg.end_location;

        //console.log(leg.start_address);
        //console.log(leg.end_address);
        //console.log(leg.start_location);
        //console.log(leg.end_location);

        const iconA = getEndIcon(true);
        const rm1 = new google.maps.Marker({
            map: map,
            icon: iconA,
            position: loc1
        });
        const iconB = getEndIcon(false);
        const rm2 = new google.maps.Marker({
            map: map,
            icon: iconB,
            position: loc2
        });
        setRouteMarker1(rm1);
        setRouteMarker2(rm2);
    }

    const clearPoiMarkers = () => {
        //console.log("Clearing Markers");
        if (poiMarkers) {
            for (var marker of poiMarkers) {
                marker.setMap(null);
            }
            setPoiMarkers(null);
        }
    }

    const computeMidPoint = (directionResult: google.maps.DirectionsResult) => {
        const totalDistance = getTotalDistance(directionResult);
        const halfwayLatLng = getMidPoint(directionResult);

        const marker = getMidpointMarker(map, halfwayLatLng);

        const route = directionResult.routes[0];
        const leg = route.legs[0];

        // save halfway marker so we can later destroy it
        setHalfwayMarker(marker);
        dispatch(setHalfway({
            latLng: halfwayLatLng.toJSON(),
            startLatLng: leg.start_location.toJSON(),
            endLatLng: leg.end_location.toJSON(),
            term: mapoptions.poi,
            totalDistance: totalDistance
        }));
    }

    const dropPin = (loc: google.maps.LatLngLiteral, poiindex: number): google.maps.Marker => {
        const icon = getSpriteIcon(poiindex + 1);
        const marker = new google.maps.Marker({
            map: map,
            icon: icon,
            position: loc,
            animation: google.maps.Animation.DROP
        });

        return marker;
    }

    const calcRouteHalfway = () => {
        directionsRenderer.setPanel(null);

        const request: google.maps.DirectionsRequest = {
            origin: mapoptions.address1,
            destination: mapoptions.address2,
            travelMode: getTravelMode(mapoptions.travelMode),
            avoidHighways: mapoptions.avoidHighways,
            avoidTolls: mapoptions.avoidTollRoads,
        };

        directionsService.route(request, function (response, status) {
            if (status === google.maps.DirectionsStatus.OK) {
                directionsRenderer.setDirections(response);
                props.setMapError(status);
            } else {
                props.setMapError(status);
            }
        });
    }

    const clickMarker = (poi) => {
        // use the react router
        const url = composePathPoi(adr1, adr2, poiname, travel, poi.result.id, mapoptions.code);
        navigate(url);
    }

    const wrapclass = mapDisplayMode === MapDisplayMode.START ? "loading googlemap" : "loaded googlemap";

    return <div className={wrapclass}>
        <div className="map" ref={myRef} />
        <DynamicMapGeocode /></div>;
}

export default GoogleMapResults;
